import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  allMutations, genes, cancerTypes, chromosomes, geneInfo, pathways,
  drugDatabase, mutationSignatures, databaseStats, cooccurrencePatterns,
  roleOptions, oncoKBLevels, mutationTypes, driverStatuses, recurrenceOptions
} from './data';
import LollipopPlot from './components/LollipopPlot';
import PathwayDiagram from './components/PathwayDiagram';
import CooccurrenceHeatmap from './components/CooccurrenceHeatmap';
import MutationCatalogTable from './components/MutationCatalogTable';

// Icons as simple SVG components
const Icons = {
  Search: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  DNA: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
  Chart: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  Beaker: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
  Upload: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>,
  Database: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>,
  External: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
  ChevronDown: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
  ChevronUp: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>,
  Fire: () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" /></svg>,
};

// Tier badge component
const TierBadge = ({ tier }) => {
  const colors = {
    'Tier I': 'bg-red-100 text-red-800 border-red-200',
    'Tier II': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Tier III': 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${colors[tier] || colors['Tier III']}`}>
      {tier}
    </span>
  );
};

// OncoKB Level badge
const OncoKBBadge = ({ level }) => {
  const colors = {
    'Level 1': 'bg-green-600 text-white',
    'Level 2': 'bg-green-400 text-white',
    'Level 3A': 'bg-yellow-500 text-white',
    'Level 3B': 'bg-yellow-400 text-black',
    'Level 4': 'bg-gray-400 text-white',
    'N/A': 'bg-gray-200 text-gray-600'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${colors[level] || colors['N/A']}`}>
      {level}
    </span>
  );
};

// Role badge
const RoleBadge = ({ role }) => {
  const colors = {
    'Oncogene': 'bg-red-100 text-red-800',
    'TSG': 'bg-blue-100 text-blue-800',
    'Other': 'bg-gray-100 text-gray-600'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${colors[role] || colors['Other']}`}>
      {role}
    </span>
  );
};

// Drug response badge
const DrugBadge = ({ response }) => {
  const colors = {
    'Sensitive': 'bg-green-100 text-green-800',
    'Resistant': 'bg-red-100 text-red-800',
    'Possibly Sensitive': 'bg-blue-100 text-blue-800',
    'Possibly Resistant': 'bg-orange-100 text-orange-800',
    'Partially Sensitive': 'bg-yellow-100 text-yellow-800'
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${colors[response] || 'bg-gray-100 text-gray-800'}`}>
      {response}
    </span>
  );
};

// Expandable Row Detail Component
const ExpandedRowDetail = ({ mutation }) => {
  const pathwayName = Object.entries(pathways).find(([key, val]) =>
    val.genes.includes(mutation.gene)
  )?.[1]?.name || 'N/A';

  return (
    <tr className="bg-gray-50">
      <td colSpan={8} className="px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Basic Info */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Mechanism:</span>
                <div className="font-medium text-indigo-700">{mutation.mechanism}</div>
              </div>
              <div>
                <span className="text-gray-500">Domain:</span>
                <div className="font-medium">{mutation.domain || 'N/A'}</div>
              </div>
              <div>
                <span className="text-gray-500">Pathway:</span>
                <div className="font-medium text-purple-700">{pathwayName}</div>
              </div>
              <div>
                <span className="text-gray-500">Sample Count:</span>
                <div className="font-medium">{mutation.sampleCount?.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-500">Consequence:</span>
                <div className="font-medium">{mutation.consequence?.replace(/_/g, ' ')}</div>
              </div>
              <div>
                <span className="text-gray-500">Recurrence:</span>
                <div className="font-medium">{mutation.recurrence}</div>
              </div>
            </div>

            {mutation.mutuallyExclusive?.length > 0 && (
              <div>
                <span className="text-gray-500 text-sm">Mutually Exclusive:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {mutation.mutuallyExclusive.map(g => (
                    <span key={g} className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">{g}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Middle Column - Drugs */}
          <div>
            <span className="text-gray-500 text-sm">Targeted Drugs:</span>
            {mutation.drugResponse?.length > 0 ? (
              <div className="mt-1 space-y-1">
                {mutation.drugResponse.map((dr, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{dr.drug}</span>
                    <DrugBadge response={dr.response} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-sm mt-1">No targeted therapies</div>
            )}

            {mutation.functionalImpact && (
              <div className="mt-3">
                <span className="text-gray-500 text-sm">Notes:</span>
                <div className="text-sm text-gray-700 mt-1">{mutation.functionalImpact}</div>
              </div>
            )}
          </div>

          {/* Right Column - External Links */}
          <div>
            <span className="text-gray-500 text-sm">External Resources:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {mutation.cosmicId && (
                <a
                  href={`https://cancer.sanger.ac.uk/cosmic/mutation/overview?id=${mutation.cosmicId.replace('COSM', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                >
                  COSMIC <Icons.External />
                </a>
              )}
              <a
                href={`https://www.oncokb.org/gene/${mutation.gene}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                OncoKB <Icons.External />
              </a>
              <a
                href={`https://www.cbioportal.org/results/mutations?gene_list=${mutation.gene}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                cBioPortal <Icons.External />
              </a>
              {mutation.pdbId && (
                <a
                  href={`https://www.rcsb.org/structure/${mutation.pdbId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 transition-colors"
                >
                  PDB <Icons.External />
                </a>
              )}
            </div>

            {mutation.clinicalTrials?.length > 0 && (
              <div className="mt-3">
                <span className="text-gray-500 text-sm">Clinical Trials:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {mutation.clinicalTrials.slice(0, 3).map(trial => (
                    <a
                      key={trial}
                      href={`https://clinicaltrials.gov/ct2/show/${trial}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded hover:bg-indigo-200"
                    >
                      {trial}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// Mutation Detail Panel (Full Modal)
const MutationDetail = ({ mutation, onClose }) => {
  if (!mutation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{mutation.gene} {mutation.hgvsp}</h2>
            <p className="text-sm text-gray-500">{mutation.hgvsc}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Chromosome</div>
              <div className="font-semibold">{mutation.chromosome}:{mutation.position}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Consequence</div>
              <div className="font-semibold">{mutation.consequence?.replace(/_/g, ' ')}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Clinical Significance</div>
              <div className="font-semibold">{mutation.clinicalSignificance}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">AMP/ASCO/CAP Tier</div>
              <div><TierBadge tier={mutation.tier} /></div>
            </div>
          </div>

          {/* Role and Driver Status */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Role</div>
              <div><RoleBadge role={mutation.role} /></div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">OncoKB Level</div>
              <div><OncoKBBadge level={mutation.oncoKBLevel} /></div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Driver Status</div>
              <div className="font-semibold">{mutation.driverStatus}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-xs text-gray-500">Hotspot</div>
              <div className="font-semibold flex items-center gap-1">
                {mutation.isHotspot ? <><Icons.Fire /><span className="text-orange-600">Yes</span></> : 'No'}
              </div>
            </div>
          </div>

          {/* Cancer Types */}
          <div>
            <h3 className="font-semibold mb-2">Cancer Types</h3>
            <div className="flex flex-wrap gap-2">
              {mutation.cancerTypes?.map(ct => (
                <span key={ct} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">{ct}</span>
              ))}
            </div>
          </div>

          {/* Somatic/Germline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Origin</h3>
              <span className={`px-3 py-1 rounded text-sm ${
                mutation.somaticGermline === 'Germline' ? 'bg-purple-100 text-purple-800' :
                mutation.somaticGermline === 'Both' ? 'bg-orange-100 text-orange-800' :
                'bg-green-100 text-green-800'
              }`}>
                {mutation.somaticGermline}
              </span>
              {mutation.hereditaryRisk && (
                <p className="mt-2 text-sm text-red-600">
                  <strong>Hereditary Risk:</strong> {mutation.hereditaryRisk}
                </p>
              )}
            </div>
            <div>
              <h3 className="font-semibold mb-2">Protein Domain</h3>
              <span className="px-3 py-1 bg-gray-100 rounded text-sm">{mutation.domain || 'N/A'}</span>
            </div>
          </div>

          {/* Drug Response */}
          {mutation.drugResponse && mutation.drugResponse.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Drug Response Predictions</h3>
              <div className="space-y-2">
                {mutation.drugResponse.map((dr, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                    <span className="font-medium">{dr.drug}</span>
                    <DrugBadge response={dr.response} />
                    <span className="text-sm text-gray-500">{dr.evidence}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clinical Trials */}
          {mutation.clinicalTrials && mutation.clinicalTrials.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Active Clinical Trials</h3>
              <div className="flex flex-wrap gap-2">
                {mutation.clinicalTrials.map(trial => (
                  <a
                    key={trial}
                    href={`https://clinicaltrials.gov/ct2/show/${trial}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                  >
                    {trial} <Icons.External />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Functional Scores */}
          <div>
            <h3 className="font-semibold mb-2">Functional Scores</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-2 bg-gray-50 rounded text-center">
                <div className="text-xs text-gray-500">CADD</div>
                <div className={`font-bold ${mutation.cadd > 25 ? 'text-red-600' : mutation.cadd > 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {mutation.cadd?.toFixed(1) || 'N/A'}
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded text-center">
                <div className="text-xs text-gray-500">REVEL</div>
                <div className={`font-bold ${mutation.revel > 0.7 ? 'text-red-600' : mutation.revel > 0.4 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {mutation.revel?.toFixed(2) || 'N/A'}
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded text-center">
                <div className="text-xs text-gray-500">AlphaMissense</div>
                <div className={`font-bold ${mutation.alphaMissense > 0.7 ? 'text-red-600' : mutation.alphaMissense > 0.4 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {mutation.alphaMissense?.toFixed(2) || 'N/A'}
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded text-center">
                <div className="text-xs text-gray-500">gnomAD Freq</div>
                <div className="font-bold">
                  {mutation.gnomadFreq !== null ? (mutation.gnomadFreq === 0 ? 'Absent' : mutation.gnomadFreq.toExponential(1)) : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* External Links */}
          <div>
            <h3 className="font-semibold mb-2">External Resources</h3>
            <div className="flex flex-wrap gap-2">
              {mutation.cosmicId && (
                <a
                  href={`https://cancer.sanger.ac.uk/cosmic/mutation/overview?id=${mutation.cosmicId.replace('COSM', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
                >
                  COSMIC <Icons.External />
                </a>
              )}
              <a
                href={`https://www.oncokb.org/gene/${mutation.gene}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
              >
                OncoKB <Icons.External />
              </a>
              <a
                href={`https://www.cbioportal.org/results/mutations?gene_list=${mutation.gene}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
              >
                cBioPortal <Icons.External />
              </a>
              {mutation.pdbId && (
                <a
                  href={`https://www.rcsb.org/structure/${mutation.pdbId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
                >
                  PDB Structure <Icons.External />
                </a>
              )}
              {mutation.pubmedIds && mutation.pubmedIds.length > 0 && (
                <a
                  href={`https://pubmed.ncbi.nlm.nih.gov/${mutation.pubmedIds[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                >
                  PubMed ({mutation.pubmedIds.length}) <Icons.External />
                </a>
              )}
            </div>
          </div>

          {/* Functional Impact */}
          {mutation.functionalImpact && (
            <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-1">Functional Impact</h3>
              <p className="text-yellow-900">{mutation.functionalImpact}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState('catalog');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedMutation, setSelectedMutation] = useState(null);
  const [selectedPathway, setSelectedPathway] = useState('RTK_RAS');
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState(null);
  const [catalogStats, setCatalogStats] = useState({ totalMutations: 0, totalGenes: 0 });
  const [catalogMutations, setCatalogMutations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch('/data/mutation_catalog.json')
      .then(res => res.ok ? res.json() : [])
      .then(rows => {
        if (cancelled || !Array.isArray(rows)) return;
        const geneSet = new Set(rows.map(r => r.gene_symbol).filter(Boolean));
        setCatalogStats({ totalMutations: rows.length, totalGenes: geneSet.size });
        const simplified = rows
          .map(r => ({
            gene: r.gene_symbol,
            cancerTypes: (r.cancer_types || []).map(ct => ct.code).filter(Boolean)
          }))
          .filter(r => r.gene);
        setCatalogMutations(simplified);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Filter states
  const [filters, setFilters] = useState({
    chromosome: '',
    cancerType: '',
    pathway: '',
    role: '',
    oncoKBLevel: '',
    mutationType: '',
    driverStatus: '',
    actionable: '',
    hotspot: '',
    recurrence: '',
    gene: ''
  });

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      chromosome: '', cancerType: '', pathway: '', role: '',
      oncoKBLevel: '', mutationType: '', driverStatus: '',
      actionable: '', hotspot: '', recurrence: '', gene: ''
    });
    setSearchQuery('');
  };

  // Get pathway genes for filtering
  const getPathwayGenes = (pathwayKey) => {
    return pathways[pathwayKey]?.genes || [];
  };

  // Filter mutations
  const filteredMutations = useMemo(() => {
    return allMutations.filter(m => {
      // Text search
      const matchesSearch = !searchQuery ||
        m.gene.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.hgvsp?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.hgvsc?.toLowerCase().includes(searchQuery.toLowerCase());

      // Dropdown filters
      const matchesChromosome = !filters.chromosome || m.chromosome === filters.chromosome;
      const matchesCancer = !filters.cancerType || m.cancerTypes?.includes(filters.cancerType);
      const matchesPathway = !filters.pathway || getPathwayGenes(filters.pathway).includes(m.gene);
      const matchesRole = !filters.role || m.role === filters.role;
      const matchesOncoKB = !filters.oncoKBLevel || m.oncoKBLevel === filters.oncoKBLevel;
      const matchesMutationType = !filters.mutationType || m.consequence === filters.mutationType;
      const matchesDriver = !filters.driverStatus || m.driverStatus === filters.driverStatus;
      const matchesActionable = !filters.actionable ||
        (filters.actionable === 'Yes' && m.isActionable) ||
        (filters.actionable === 'No' && !m.isActionable);
      const matchesHotspot = !filters.hotspot ||
        (filters.hotspot === 'Yes' && m.isHotspot) ||
        (filters.hotspot === 'No' && !m.isHotspot);
      const matchesRecurrence = !filters.recurrence || m.recurrence === filters.recurrence;
      const matchesGene = !filters.gene || m.gene === filters.gene;

      return matchesSearch && matchesChromosome && matchesCancer && matchesPathway &&
             matchesRole && matchesOncoKB && matchesMutationType && matchesDriver &&
             matchesActionable && matchesHotspot && matchesRecurrence && matchesGene;
    });
  }, [searchQuery, filters]);

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Batch query handler
  const handleBatchQuery = useCallback(() => {
    const queries = batchInput.split('\n').filter(q => q.trim());
    const results = queries.map(query => {
      const trimmed = query.trim().toUpperCase();
      const matches = allMutations.filter(m =>
        m.gene.toUpperCase() === trimmed ||
        m.hgvsp?.toUpperCase().includes(trimmed) ||
        m.hgvsc?.toUpperCase().includes(trimmed)
      );
      return { query: query.trim(), matches };
    });
    setBatchResults(results);
  }, [batchInput]);

  // Cancer type distribution for selected gene
  const cancerDistribution = useMemo(() => {
    if (!filters.gene) return [];
    const counts = {};
    allMutations
      .filter(m => m.gene === filters.gene)
      .forEach(m => {
        m.cancerTypes?.forEach(ct => {
          counts[ct] = (counts[ct] || 0) + 1;
        });
      });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [filters.gene]);

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v).length + (searchQuery ? 1 : 0);
  const pathwayMutations = catalogMutations.length ? catalogMutations : allMutations;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icons.DNA />
              <div>
                <h1 className="text-2xl font-bold">Cancer Mutation Catalog</h1>
                <p className="text-indigo-200 text-sm">Comprehensive database of driver mutations</p>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-indigo-200">{catalogStats.totalMutations || databaseStats.totalMutations} Mutations</div>
              <div className="text-indigo-200">{catalogStats.totalGenes || databaseStats.totalGenes} Genes</div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'catalog', label: 'Catalog', icon: Icons.Database },
              { id: 'visualize', label: 'Visualization', icon: Icons.Chart },
              { id: 'clinical', label: 'Clinical Utility', icon: Icons.Beaker },
              { id: 'analysis', label: 'Analysis Tools', icon: Icons.Upload }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Catalog Tab */}
        {activeTab === 'catalog' && (
          <MutationCatalogTable />
        )}

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Icons.Search />
                  </div>
                  <input
                    type="text"
                    placeholder="Search gene, mutation, or variant..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Clear All ({activeFilterCount})
                  </button>
                )}
              </div>
            </div>

            {/* Filter Row 1 */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <select
                  value={filters.chromosome}
                  onChange={e => updateFilter('chromosome', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Chromosomes</option>
                  {chromosomes.map(c => <option key={c} value={c}>Chr {c}</option>)}
                </select>

                <select
                  value={filters.cancerType}
                  onChange={e => updateFilter('cancerType', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Cancer Types</option>
                  {cancerTypes.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>

                <select
                  value={filters.pathway}
                  onChange={e => updateFilter('pathway', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Pathways</option>
                  {Object.entries(pathways).map(([key, val]) => (
                    <option key={key} value={key}>{val.name}</option>
                  ))}
                </select>

                <select
                  value={filters.role}
                  onChange={e => updateFilter('role', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Oncogene/TSG</option>
                  {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>

                <select
                  value={filters.oncoKBLevel}
                  onChange={e => updateFilter('oncoKBLevel', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All OncoKB Levels</option>
                  {oncoKBLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>

            {/* Filter Row 2 */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <select
                  value={filters.mutationType}
                  onChange={e => updateFilter('mutationType', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Mutation Types</option>
                  {mutationTypes.map(mt => (
                    <option key={mt} value={mt}>{mt.replace(/_/g, ' ')}</option>
                  ))}
                </select>

                <select
                  value={filters.driverStatus}
                  onChange={e => updateFilter('driverStatus', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Driver Status</option>
                  {driverStatuses.map(ds => <option key={ds} value={ds}>{ds}</option>)}
                </select>

                <select
                  value={filters.actionable}
                  onChange={e => updateFilter('actionable', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Actionability</option>
                  <option value="Yes">Actionable</option>
                  <option value="No">Not Actionable</option>
                </select>

                <select
                  value={filters.hotspot}
                  onChange={e => updateFilter('hotspot', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Hotspot Status</option>
                  <option value="Yes">Hotspot</option>
                  <option value="No">Non-Hotspot</option>
                </select>

                <select
                  value={filters.recurrence}
                  onChange={e => updateFilter('recurrence', e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Recurrence</option>
                  {recurrenceOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between text-gray-600">
              <span>Showing {Math.min(filteredMutations.length, 100)} of {filteredMutations.length} mutations</span>
              {filteredMutations.length !== allMutations.length && (
                <span className="text-sm text-indigo-600">
                  Filtered from {allMutations.length} total
                </span>
              )}
            </div>

            {/* Mutation Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-8"></th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Gene</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mutation</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">OncoKB</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cancer Types</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredMutations.slice(0, 100).map(m => (
                      <React.Fragment key={m.id}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer ${expandedRows.has(m.id) ? 'bg-indigo-50' : ''}`}
                          onClick={() => toggleRowExpansion(m.id)}
                        >
                          <td className="px-2 py-3 text-center">
                            {expandedRows.has(m.id) ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="font-semibold text-indigo-600">{m.gene}</span>
                              {m.isHotspot && <span className="text-orange-500"><Icons.Fire /></span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{m.hgvsp}</div>
                            <div className="text-xs text-gray-500">{m.hgvsc}</div>
                          </td>
                          <td className="px-4 py-3"><RoleBadge role={m.role} /></td>
                          <td className="px-4 py-3"><OncoKBBadge level={m.oncoKBLevel} /></td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${
                              m.driverStatus === 'Driver' ? 'text-red-600 font-medium' :
                              m.driverStatus === 'Likely Driver' ? 'text-orange-600' : 'text-gray-500'
                            }`}>
                              {m.driverStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3"><TierBadge tier={m.tier} /></td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {m.cancerTypes?.slice(0, 2).map(ct => (
                                <span key={ct} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{ct}</span>
                              ))}
                              {m.cancerTypes?.length > 2 && (
                                <span className="text-xs text-gray-500">+{m.cancerTypes.length - 2}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(m.id) && <ExpandedRowDetail mutation={m} />}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredMutations.length > 100 && (
                <div className="p-4 bg-gray-50 text-center text-gray-600">
                  Showing 100 of {filteredMutations.length} results. Use filters to narrow down.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Visualization Tab */}
        {activeTab === 'visualize' && (
          <div className="space-y-6">
            {/* Gene Selection for Lollipop */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Protein Domain Visualization</h3>
              <select
                value={filters.gene}
                onChange={e => updateFilter('gene', e.target.value)}
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a gene...</option>
                {genes.filter(g => geneInfo[g]).map(g => (
                  <option key={g} value={g}>{g} - {geneInfo[g]?.name}</option>
                ))}
              </select>
            </div>

            {/* Lollipop Plot */}
            {filters.gene && geneInfo[filters.gene] && (
              <LollipopPlot
                gene={filters.gene}
                mutations={allMutations}
                geneInfo={geneInfo}
                width={900}
                height={350}
              />
            )}

            {/* Cancer Distribution */}
            {filters.gene && cancerDistribution.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold mb-3">Cancer Type Distribution for {filters.gene}</h3>
                <div className="space-y-2">
                  {cancerDistribution.map(([cancer, count]) => (
                    <div key={cancer} className="flex items-center gap-3">
                      <div className="w-32 text-sm text-gray-700">{cancer}</div>
                      <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded"
                          style={{ width: `${(count / cancerDistribution[0][1]) * 100}%` }}
                        />
                      </div>
                      <div className="w-10 text-sm text-gray-600 text-right">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pathway Diagram */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Pathway Analysis</h3>
              <select
                value={selectedPathway}
                onChange={e => setSelectedPathway(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 mb-4"
              >
                {Object.entries(pathways).map(([key, val]) => (
                  <option key={key} value={key}>{val.name}</option>
                ))}
              </select>
              <PathwayDiagram
                pathway={selectedPathway}
                mutations={pathwayMutations}
                pathwayData={pathways}
              />
            </div>

            {/* Co-occurrence Heatmap */}
            <CooccurrenceHeatmap mutations={pathwayMutations} topGenes={15} />
          </div>
        )}

        {/* Clinical Utility Tab */}
        {activeTab === 'clinical' && (
          <div className="space-y-6">
            {/* Drug Response Database */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Targeted Therapies Database</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(drugDatabase).slice(0, 12).map(([drug, info]) => (
                  <div key={drug} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <h4 className="font-semibold text-indigo-600">{drug}</h4>
                    <div className="text-sm text-gray-600 mt-1">Target: {info.target}</div>
                    <div className="text-sm text-gray-600">Class: {info.class}</div>
                    <div className="text-sm text-gray-600">Indication: {info.indication}</div>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded ${
                      info.approval === 'FDA Approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {info.approval}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* AMP/ASCO/CAP Tiering Guide */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Variant Interpretation Guidelines (AMP/ASCO/CAP)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border-l-4 border-red-500 p-4 bg-red-50">
                  <h4 className="font-semibold text-red-800">Tier I - Strong Clinical Significance</h4>
                  <p className="text-sm text-red-700 mt-2">
                    FDA-approved therapies or professional guidelines recommend specific treatment based on this biomarker.
                  </p>
                  <div className="mt-2 text-sm font-medium">{databaseStats.mutationsByTier.tierI} mutations</div>
                </div>
                <div className="border-l-4 border-yellow-500 p-4 bg-yellow-50">
                  <h4 className="font-semibold text-yellow-800">Tier II - Potential Clinical Significance</h4>
                  <p className="text-sm text-yellow-700 mt-2">
                    Clinical trials available or evidence from other tumor types. May qualify for off-label use.
                  </p>
                  <div className="mt-2 text-sm font-medium">{databaseStats.mutationsByTier.tierII} mutations</div>
                </div>
                <div className="border-l-4 border-gray-500 p-4 bg-gray-50">
                  <h4 className="font-semibold text-gray-800">Tier III - Unknown Significance</h4>
                  <p className="text-sm text-gray-700 mt-2">
                    Variant of unknown significance. May have diagnostic or prognostic value pending further evidence.
                  </p>
                  <div className="mt-2 text-sm font-medium">{databaseStats.mutationsByTier.tierIII} mutations</div>
                </div>
              </div>
            </div>

            {/* Hereditary Risk */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Germline Mutations with Hereditary Risk</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Gene</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Mutation</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Syndrome/Risk</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allMutations
                      .filter(m => m.hereditaryRisk)
                      .slice(0, 15)
                      .map(m => (
                        <tr key={m.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-semibold text-indigo-600">{m.gene}</td>
                          <td className="px-4 py-2">{m.hgvsp}</td>
                          <td className="px-4 py-2 text-sm text-red-600">{m.hereditaryRisk}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Tools Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {/* Batch Query */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Batch Query</h3>
              <p className="text-sm text-gray-600 mb-3">
                Enter gene names or mutations (one per line) to find matches in the database.
              </p>
              <textarea
                value={batchInput}
                onChange={e => setBatchInput(e.target.value)}
                placeholder="Enter queries, e.g.:&#10;EGFR&#10;KRAS G12C&#10;TP53 R175H&#10;BRAF V600E"
                className="w-full h-32 border rounded-lg p-3 font-mono text-sm focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleBatchQuery}
                className="mt-3 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Search Database
              </button>

              {batchResults && (
                <div className="mt-4 space-y-3">
                  <h4 className="font-semibold">Results:</h4>
                  {batchResults.map((result, i) => (
                    <div key={i} className="border rounded p-3">
                      <div className="font-medium text-indigo-600">{result.query}</div>
                      {result.matches.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {result.matches.slice(0, 5).map(m => (
                            <div
                              key={m.id}
                              className="text-sm p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                              onClick={() => setSelectedMutation(m)}
                            >
                              {m.gene} {m.hgvsp} - <TierBadge tier={m.tier} />
                            </div>
                          ))}
                          {result.matches.length > 5 && (
                            <div className="text-sm text-gray-500">+{result.matches.length - 5} more</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 mt-1">No matches found</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mutation Signatures */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">COSMIC Mutation Signatures</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mutationSignatures.map(sig => (
                  <div key={sig.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-mono">{sig.id}</span>
                      <span className="font-semibold">{sig.name}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{sig.etiology}</p>
                    <div className="text-xs text-gray-500">
                      <strong>Cancer types:</strong> {sig.cancerTypes.join(', ')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      <strong>Characteristic:</strong> {sig.characteristic}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Database Statistics */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Database Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <div className="text-3xl font-bold text-indigo-600">{databaseStats.totalMutations}</div>
                  <div className="text-sm text-gray-600">Total Mutations</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{databaseStats.totalGenes}</div>
                  <div className="text-sm text-gray-600">Genes Covered</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">{databaseStats.actionableMutations}</div>
                  <div className="text-sm text-gray-600">Actionable Mutations</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600">{databaseStats.hotspotMutations}</div>
                  <div className="text-sm text-gray-600">Hotspot Mutations</div>
                </div>
              </div>

              {/* By Role */}
              <div className="mt-6">
                <h4 className="font-semibold mb-3">By Gene Role</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-red-600">{databaseStats.mutationsByRole.oncogene}</div>
                    <div className="text-xs text-gray-600">Oncogene</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-blue-600">{databaseStats.mutationsByRole.tsg}</div>
                    <div className="text-xs text-gray-600">Tumor Suppressor</div>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg text-center">
                    <div className="text-xl font-bold text-gray-600">{databaseStats.mutationsByRole.other}</div>
                    <div className="text-xs text-gray-600">Other</div>
                  </div>
                </div>
              </div>

              {/* Mutation Type Breakdown */}
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Mutation Types</h4>
                <div className="space-y-2">
                  {Object.entries(databaseStats.mutationsByType)
                    .filter(([, count]) => count > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => (
                      <div key={type} className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-700 capitalize">{type}</div>
                        <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded"
                            style={{ width: `${(count / databaseStats.totalMutations) * 100}%` }}
                          />
                        </div>
                        <div className="w-16 text-sm text-gray-600 text-right">{count}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Mutation Detail Modal */}
      {selectedMutation && (
        <MutationDetail
          mutation={selectedMutation}
          onClose={() => setSelectedMutation(null)}
        />
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">Cancer Mutation Catalog - For Research Use Only</p>
          <p className="text-xs mt-2">Data derived from ClinVar, COSMIC, cBioPortal, OncoKB, and published literature</p>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <a href="https://www.oncokb.org" target="_blank" rel="noopener noreferrer" className="hover:text-white">OncoKB</a>
            <a href="https://www.cbioportal.org" target="_blank" rel="noopener noreferrer" className="hover:text-white">cBioPortal</a>
            <a href="https://cancer.sanger.ac.uk/cosmic" target="_blank" rel="noopener noreferrer" className="hover:text-white">COSMIC</a>
            <a href="https://clinicaltrials.gov" target="_blank" rel="noopener noreferrer" className="hover:text-white">ClinicalTrials.gov</a>
            <a href="https://gnomad.broadinstitute.org" target="_blank" rel="noopener noreferrer" className="hover:text-white">gnomAD</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
